import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Modal,
  Button,
  Table,
  Popover,
  Space,
  Empty,
  Typography,
  Popconfirm,
  Badge,
  Input,
  notification,
} from 'antd';

import {
  selectors,
  ForwardContract,
  closeForwardContract,
  PAUSED,
  FROZEN,
  MARKET_ORDER,
  STOP_LOSS,
  TAKE_PROFIT,
} from 'state';
import { formatters } from 'services';
import { cashExchange, useReportingCurrency } from '../../state/app/utils';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { TableColumnsType as ColumnsType } from 'antd';
import FwdOdaModal from './FwdOdaModal';
import { currencies } from '../../state/app/selectors';

const { TextArea } = Input;

type props = {
  visible: boolean;
  hide: () => void;
  id: number;
  player_id?: number;
};

type displayContract = {
  contract: ForwardContract;
  key: string;
  name: string;
  open_date: string;
  currency: string;
  open_price: string;
  amount: string;
  reporting_unrealized_pnl: string;
  value: string;
  investment_rationale: string;
};
// FWD CONTRACTS (show contracts modal)
const ContractsModal: React.FC<props> = ({ visible, hide, id, player_id }) => {
  const dispatch = useDispatch();
  const contracts = useSelector(
    selectors.forwardContractsOfInstrumentList(id, player_id)
  );
  const fin = useSelector(selectors.financialInstrumentById(id, player_id));
  const asset = useSelector(selectors.underlyingAssetOfInstrument(fin));
  const currency = useSelector(selectors.currencyById(asset.base_currency));
  const session = useSelector(selectors.activeSession);
  const reportingCurrency = useReportingCurrency(session.id);
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);
  const scenarioDate = new Date(session.scenario_date.split('T')[0]);
  const [visibleOdaModal, setVisibleOdaModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<
    displayContract | undefined
  >(undefined);
  const [investmentRationale, setInvestmentRationale] = useState<string>('');

  const data: displayContract[] = contracts
    .map(contract => {
      const reporting_value = cashExchange(
        asset.base_currency,
        session.reporting_currency,
        contract.amount,
        0,
        assetList,
        currencyList
        // contract.open_price
      );

      return {
        contract: contract,
        key: contract.open_date,
        name: asset.name,
        open_date: contract.open_date.split('T')[0],
        currency: currency.symbol,
        open_price: formatters.priceFormat(
          contract.open_price,
          asset.ticker,
          currency.symbol,
          asset.resourcetype
        ),
        amount: formatters.commas2Digits(contract.amount),
        reporting_unrealized_pnl: formatters.commas2Digits(
          contract.reporting_unrealized_pnl
        ),
        value: formatters.commas2Digits(-1 * reporting_value),
        investment_rationale: contract.investment_rationale,
      };
    })
    .filter(contract => {
      // if contract doesn't have a delivery_timestamp it means it has not yet matured
      return contract.contract.delivery_timestamp === null;
    });

  let satisfiesInvestmentRationaleRequirement: boolean;
  let investmentRationalePlaceholder: string;
  if (session.require_investment_rationale === false) {
    satisfiesInvestmentRationaleRequirement = true;
    investmentRationalePlaceholder = 'Investment Rationale';
  } else {
    investmentRationalePlaceholder =
      'Investment Rationale | required field min. 10 char.';
    if (
      investmentRationale !== undefined &&
      investmentRationale !== '' &&
      investmentRationale.length > 9
    ) {
      satisfiesInvestmentRationaleRequirement = true;
    } else {
      satisfiesInvestmentRationaleRequirement = false;
    }
  }

  const ActionColumnTitle = <Space>Action/Status</Space>;
  const handleCloseContract = (contract: displayContract) => {
    dispatch(
      closeForwardContract({
        contract_id: contract.contract.id,
        investment_rationale: investmentRationale,
      })
    );
  };
  const ActionButton = (contract: displayContract) => {
    const isClosingContract = contract.contract.closing_contract_for !== null;
    const isClosed = contract.contract.closed_by !== null;
    const sessionPaused = session.status === PAUSED;
    if (isClosingContract) {
      if (contract.contract.type === MARKET_ORDER) {
        return `Offsetting #${contract.contract.closing_contract_for}`;
      } else if (contract.contract.type === STOP_LOSS) {
        return `Stop loss for #${contract.contract.closing_contract_for}`;
      } else if (contract.contract.type === TAKE_PROFIT) {
        return `Take profit for #${contract.contract.closing_contract_for}`;
      }
    }
    if (isClosed) {
      return `Offset by #${contract.contract.closed_by}`;
    }
    const title = (
      <div>
        <div>Are you sure?</div>
        <div style={{ width: 250 }}>
          Offsetting a forward contract involves initiating a new, opposing
          contract to mitigate the risk associated with the original contract.
        </div>
        <br />
        <TextArea
          maxLength={511}
          minLength={10}
          placeholder={investmentRationalePlaceholder}
          value={investmentRationale}
          onChange={e => setInvestmentRationale(e.target.value)}
        />
      </div>
    );
    if (player_id) return '-';
    return (
      <div>
        <Popconfirm
          title={title}
          onConfirm={() => {
            if (!satisfiesInvestmentRationaleRequirement) {
              notification.warning({
                message:
                  'Must provide investment rationale, minimum 10 characters.',
              });
            } else {
              handleCloseContract(contract);
            }
          }}
          disabled={sessionPaused || session.status === FROZEN}
        >
          <Button disabled={sessionPaused || session.status === FROZEN}>
            Offset contract
          </Button>
        </Popconfirm>
        <Button
          style={{ marginLeft: 10 }}
          disabled={sessionPaused || session.status === FROZEN}
          onClick={() => {
            setSelectedContract(contract);
            setVisibleOdaModal(true);
          }}
        >
          <div>
            {/*ODA*/}
            <Badge
              // style={{ marginLeft: 10 }}
              status={contract.contract.oda ? 'processing' : 'default'}
              color={contract.contract.oda ? 'green' : 'orange'}
              text={contract.contract.oda ? 'ODA' : 'ODA'}
            />
          </div>
        </Button>
      </div>
    );
  };

  const columns: ColumnsType<displayContract> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 120,
    },
    {
      title: 'Open',
      dataIndex: 'open_date',
      width: 60,
    },
    {
      title: 'Maturity',
      dataIndex: 'contract',
      render: (text, record) => {
        return record.contract ? record.contract.maturity_date : null;
      },
      width: 60,
    },
    {
      title: 'CCY',
      dataIndex: 'currency',
    },
    {
      title: 'Open Price',
      dataIndex: 'open_price',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
    },
    {
      title: `Unrealized, ${reportingCurrency.symbol}`,
      dataIndex: 'reporting_unrealized_pnl',
      render: (text, record) => {
        return record.contract
          ? formatters.commasNoDigits(record.contract.reporting_unrealized_pnl)
          : null;
      },
      width: 120,
    },
    {
      title: `Realized, ${reportingCurrency.symbol}`,
      dataIndex: 'contract',
      render: (text, record) => {
        return record.contract
          ? formatters.commasNoDigits(record.contract.reporting_realized_pnl)
          : null;
      },
      width: 100,
    },
    {
      title: `Value, ${reportingCurrency.symbol}`,
      dataIndex: 'value',
    },
    {
      title: 'Investment rationale',
      dataIndex: 'investment_rationale',
      key: 'investment_rationale',
      width: 300,
      render: text => (
        <div style={{ maxWidth: 300, whiteSpace: 'normal' }}>
          {text || '--'}
        </div>
      ),
    },
  ];
  // {
  //   title: ActionColumnTitle,
  //   render: (val: any, record: displayContract) => ActionButton(record),
  // },
  // if (player_id === undefined) {
  columns.push({
    title: ActionColumnTitle,
    render: (val: any, record: displayContract) => ActionButton(record),
    width: 150,
  });
  // }
  columns.push({
    title: 'ID',
    dataIndex: 'contract',
    render: (val, record) => record.contract.id,
  });
  // ];
  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          {`You don't have any active forward contracts yet.`}
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  return (
    <Modal
      open={visible}
      title={`Your active ${asset.name} Forward Contracts`}
      onOk={hide}
      onCancel={hide}
      width={'90%'}
      // styles={{ body: { padding: 20, maxHeight: '80vh', overflowY: 'auto' } }} // Ensure modal is scrollable
      styles={{ body: { padding: 20, maxHeight: '80vh' } }} // Ensure modal is scrollable
    >
      <Table
        // tableLayout="fixed" // Ensure fixed layout to respect column widths
        dataSource={data}
        columns={columns}
        bordered
        locale={noDataBehaviour}
        scroll={{ y: '70vh', x: 'max-content' }} // Add horizontal scroll
      ></Table>
      {visibleOdaModal && (
        <FwdOdaModal
          visible={visibleOdaModal}
          hide={() => setVisibleOdaModal(false)}
          contract_id={selectedContract?.contract.id}
          instrument_id={fin.id}
        />
      )}
    </Modal>
  );
};

export default ContractsModal;
